/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { type AxiosInstance, isAxiosError } from 'axios';
import { requestBaseURL, requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { isServer } from '@/utils/isServer';
import type {
  FacetModel,
  ProductSearchResponseModel,
  SearchRequestModel,
  SkuRecordModelACES,
  VisualPartTypeModel,
} from '@/api/types/browse-search-types';
import { getAxios } from '@/lib/axios';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import {
  type BaseInfiniteMergeFunction,
  type BaseMergedData,
  createInfiniteQuery,
} from '@/utils/query/createInfiniteQuery';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useRouter } from 'next/navigation';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { type AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { getApiOptions } from '../utils/getSearchApiOptions';
import { getMapFacet, useMapFacet } from '@/hooks/useMapFacet';
import { type QueryClient } from '@tanstack/react-query';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import type { MappedSearchProductData } from '../interface';
import { usePageType } from '@/hooks/usePageType';
import { useGlobalState } from '@/hooks/useGlobalState';
import logger from '@/utils/logger';
import { countryCodes } from '@/constants/locale';
import { useAppState } from '@/stores/AppState';
import { routePaths } from '@/constants/routePaths';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useRetryConfigForCategory } from '@/components/RetryConfigContext';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { constructPartGroupImageUrl } from '@/utils/common';
import { useQueryResultOnError } from '@/hooks/query/useQueryResultOnError';
import { sessionStorage } from '@/utils/sessionStorage';
import {
  getExperimentId,
  getSearchExperimentConfig,
  selectSearchExperimentUrl,
  buildSearchExperimentHeaders,
  buildSearchExperimentParams,
} from '@/utils/searchExperiment';
import { useVertexPersonalizationParams } from '../hooks/useVertexPersonalizationParams';
import { getSHA256 } from '@/utils/getSHA256';
import { useSearchServingConfig } from '../hooks/useSearchServingConfig';
import { useExperimentId } from '@/utils/searchExperiment/useExperimentId';
import environmentConfig from '@/config/environment';

const STALE_TIME_30_MINUTES = 30 * 60000;
const PART_TYPES_URL = `${requestBaseURL}/external/product-discovery/browse-search/v1/products/search`;
const ACES_PART_TYPES_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-browse-search-data/v1/products/search`;
const ES_ACES_PART_TYPES_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-browse-search-data/v2/products/search`;
const BR_ACES_PART_TYPES_URL = `${requestBaseURLGCPExtOrInt}/sls/pd/product-browse-search/v1/products/search`;
const validSortingOptions = new Set([
  'featured',
  'price-asc',
  'price-desc',
  'recently-added-desc',
  'customer-rating-desc',
  'best-seller-asc',
  'best-seller-desc',
  'recently-added',
  'customer-rating',
  'best-sellers',
]);

const brValidSortingOptions = new Set([
  'featured',
  'price-asc',
  'price-desc',
  'recently-added',
  'customer-rating',
  'best-sellers',
]);

type GetSearchProductDataOptions = {
  mapFacet: ReturnType<typeof useMapFacet>;
  locale: Locale;
  facet: string | undefined;
  minPrice?: number;
  maxPrice?: number;
  recordsPerPage: number | undefined;
  searchText: string | undefined;
  sort:
    | {
        sortFieldName: string | undefined;
        sortOrder: string | undefined;
      }
    | string
    | undefined;
  storeId: string | undefined;
  userSegment: string | undefined;
  baseVehicle?: {
    year?: number | string;
    makeId?: number;
    modelId?: number;
    vehicleTypeID?: number;
  };
  vehicleId: string | undefined;
  preview: boolean;
  skipPdpRedirect: boolean;
  userProfileId?: string;
  eCookieId: string;
  rewardsId?: string;
  vehicleQuestions?: Record<string, string>;
  ignoreVehicleSpecificProductsCheck?: boolean;
  acesEnabled: boolean;
  experimentId?: string;
  visitorId?: string;
  userId?: string;
  servingConfig?: string;
};

export const searchProductDataSelector = (
  {
    searchResults,
    redirectUrl,
    redirectPageType,
    oemBrandName,
    oemPartNumber,
    lwTestExperience,
    xFusionQueryId,
    attributionToken,
    filter,
    pbsSearchEngine,
  }: ProductSearchResponseModel,
  mapFacet: ReturnType<typeof useMapFacet> | undefined,
  acesEnabled: boolean,
  locale: Locale,
): MappedSearchProductData => {
  return {
    redirectUrl,
    redirectPageType,
    oemBrandName,
    oemPartNumber,
    lwTestExperience,
    xFusionQueryId,
    attributionToken,
    filter,
    pbsSearchEngine,
    isUnitOfMeasureMatch: searchResults?.isUnitOfMeasureMatch,
    interchangeSearchFlag: searchResults?.interchangeSearchFlag,
    correctedTerm: searchResults?.correctedTerm,
    recordsPerPage: searchResults?.recordsPerPage,
    total: searchResults?.totalNumberOfRecords,
    quickFilters: searchResults?.quickFilters,
    firstRecNum: searchResults?.firstRecordNumber,
    lastRecNum: searchResults?.lastRecordNumber,
    makeName: searchResults?.makeName,
    modelName: searchResults?.modelName,
    makeId: searchResults?.makeId,
    modelId: searchResults?.modelId,
    makeYear: searchResults?.year,
    partTypeRecords: acesEnabled
      ? (searchResults?.visualCategories?.map((item) => ({
          documentId: item.documentId,
          eCommerceProductId: item.partGroupId,
          interchangeSearchFlag: false,
          itemDescription: item.partGroupName || '',
          itemId: item.partTerminologyId || '',
          itemImageUrl: item.productImageUrl || '',
          partTypeName: item.partGroupName || '',
          productCanonicalUrl: item.canonicalUrl || '',
          productImageUrl: constructPartGroupImageUrl(item.partGroupId, locale) || '',
          productSeoUrl: item.productSeoUrl || '',
          score: item.score,
          taxonomyPath: item.taxonomyPath || '',
        })) as VisualPartTypeModel[] | undefined)
      : searchResults?.partTypeRecords,
    staticNavigation: searchResults?.facets?.map(
      mapFacet ?? ((facet: FacetModel) => getMapFacet(facet, 'Clearance')),
    ),
    searchedKeyword: searchResults?.searchedKeyword,
    records: searchResults?.skuRecords?.map?.((record) => ({
      ...(acesEnabled &&
        record.productAttributes && {
          productAttributes: record.productAttributes,
        }),
      active: record.activeFlag,
      ...(acesEnabled && record?.badges && { badges: record?.badges }),
      alternatePartNumber: record.alternatePartNumber,
      application: record.applicationQuestions?.join?.(', ') ?? '',
      brandName: record.brandName,
      brandId: record.brandId,
      positionId: record.positionId,
      citrusAdId: record.citrusAdId,
      seoUrl: record.productDetailsPageUrl,
      imageUrl: record.itemImageUrl ?? '',
      productImageUrl: acesEnabled ? (record.productImageUrl ?? '') : (record.itemImageUrl ?? ''),
      description: record.itemDescription,
      skuNumber: Number(record.itemId),
      lineCode: record.lineCode,
      systemCode: Number(record.originalSystemCode),
      partNumber: record.partNumber,
      partGroupId: record.partGroupId,
      partTerminologyId: record.partTerminologyId,
      ...(acesEnabled &&
        record.uniqueId && {
          uniqueId: record.uniqueId,
        }),
      partTypeName: acesEnabled
        ? (record.partGroupName ?? record.partTerminologyName)
        : record.partTypeName,
      productRepositoryId: record.eCommerceProductId,
      productId: acesEnabled ? record.partTerminologyId : record.eCommerceProductId,
      quickNote: record.quickNote,
      techNote: record.technicalNote,
      recordType: record.recordType,
      skuReviewEnabled: record.reviewEnabledFlag,
      configurableSkuFlag: record.configurableSkuFlag,
      vehicleFitment: record.vehicleFitment?.vehicleFit,
      vehicleFitmentLabel: record.vehicleFitmentLabel,
      vehicleSpecific: record.vehicleSpecificFlag,
      warrantyMonths: record.warrantyMonths,
      locationFilter: record.itemLocation || record.location,
      brand: record.brandName ?? '',
      name: record.itemDescription,
      productReviewsEnabled: record.reviewEnabledFlag,
      vehicleFit: (record.vehicleFitmentFlag || !!record.vehicleFitment?.vehicleFit) ?? false,
      originalPartTypeId: record.originalPartTypeId ?? '',
      productCanonicalUrl: record.productCanonicalUrl ?? '',
      productSeoUrl: record.productSeoUrl ?? '',
      interchangeSearchFlag: record.interchangeSearchFlag ?? false,
      oemBrandName: record.oemBrandName,
      oemPartNumber: record.oemPartNumber,
      warrantyText: record.warrantyText,
      sponsoredProductFlag: record.sponsoredProductFlag,
      bonusRewardAmount: record?.bonusRewardAmount,
      bonusRewardCopy: record.bonusRewardCopy,
      bonusRewardStartDate: record.bonusRewardStartDate,
      bonusRewardEndDate: record.bonusRewardEndDate,
      bonusRewardLegal: record.bonusRewardLegal,
      bonusRewardEligible: record.bonusRewardEligible,
      bonusRewardLimit: record.bonusRewardLimit,
      ...(!!(record as SkuRecordModelACES).warrantyType && {
        warrantyType: (record as SkuRecordModelACES).warrantyType,
      }),
      ...(!!(record as SkuRecordModelACES).displayNotesTypes && {
        displayNotesTypes: (record as SkuRecordModelACES).displayNotesTypes,
      }),
    })),
  };
};
const getSearchProductData = async (
  options: GetSearchProductDataOptions,
  pageNumber: number,
  axiosInstance?: AxiosInstance,
) => {
  const {
    locale,
    searchText,
    userProfileId,
    eCookieId,
    rewardsId,
    ignoreVehicleSpecificProductsCheck,
    acesEnabled,
    minPrice,
    maxPrice,
    experimentId,
    servingConfig,
    ...rest
  } = options;
  const country = getCountryFromLocale(locale);
  const customerType = 'B2C';
  const salesChannel = 'ECOMM';
  if (!searchText) {
    throw new Error('searchText is required');
  }
  const reqData: SearchRequestModel = {
    ...rest,
    pageNumber,
    country,
    customerType,
    salesChannel,
    searchText,
    userProfileId,
    // Only add minPrice/maxPrice for ACES
    ...(acesEnabled && minPrice !== undefined && { minPrice }),
    ...(acesEnabled && maxPrice !== undefined && { maxPrice }),
    ...(ignoreVehicleSpecificProductsCheck !== undefined && {
      ignoreVehicleSpecificProductsCheck,
    }),
  };

  const normalizedExperimentId = getExperimentId(experimentId, acesEnabled);
  const searchExpConfig = getSearchExperimentConfig({
    experimentId: normalizedExperimentId,
    experimentEnabled: acesEnabled,
  });

  const finalURL = acesEnabled
    ? country === 'BRA'
      ? BR_ACES_PART_TYPES_URL
      : selectSearchExperimentUrl(searchExpConfig, ACES_PART_TYPES_URL, ES_ACES_PART_TYPES_URL)
    : PART_TYPES_URL;

  // Hash userId if provided (for server-side calls that pass unhashed userId)
  const hashedUserId = options.userId ? await getSHA256(options.userId) : '';

  const enhancedReqData = buildSearchExperimentParams(searchExpConfig, reqData, {
    visitorId: options.visitorId,
    userId: hashedUserId,
  });

  const acesHeaders = buildSearchExperimentHeaders(searchExpConfig);

  const response = await getAxios(axiosInstance).post<ProductSearchResponseModel>(
    finalURL,
    enhancedReqData,
    {
      withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
      headers: {
        ...acesHeaders,
        eCookieId,
        'pbs-search-engine': 'test',
        ...(!!servingConfig && { servingConfig }),
        ...(!!rewardsId && { rewardsId }),
      },
    },
  );

  // Extract PBS search engine from response header
  const pbsSearchEngine: string | undefined =
    (response.headers['pbs-search-engine'] as string | undefined) ||
    (response.headers['Pbs-Search-Engine'] as string | undefined) ||
    'Elasticsearch';

  // Extract stageName and queryTime from response
  const stageName = response.data.searchResults?.stageName ?? '';
  const queryTime = response.data.searchResults?.queryTime;

  // Store to sessionStorage immediately when API returns it
  if (!isServer) {
    sessionStorage.setItem('pbs-search-engine', pbsSearchEngine);
    sessionStorage.setItem('search-stage-name', stageName);
    if (queryTime !== undefined) {
      sessionStorage.setItem('search-query-time', String(queryTime));
    }
  }

  return {
    ...response.data,
    pbsSearchEngine,
  };
};

const selectInfiniteQueryData: BaseInfiniteMergeFunction<ProductSearchResponseModel, number> = (
  data,
) => {
  const mergedData = data?.pages?.reduce<ProductSearchResponseModel>(
    (acc, page) => {
      if (page.searchResults) {
        return {
          ...acc,
          searchResults: {
            ...acc.searchResults,
            ...page.searchResults,
            skuRecords: [
              ...(acc.searchResults?.skuRecords ?? []),
              ...(page.searchResults?.skuRecords ?? []),
            ],
          },
          redirectUrl: page.redirectUrl,
          oemBrandName: page.oemBrandName,
          oemPartNumber: page.oemPartNumber,
          redirectPageType: page.redirectPageType,
          interchangeSearchFlag: page.interchangeSearchFlag,
          lwTestExperience: page.lwTestExperience,
          xFusionQueryId: page.xFusionQueryId,
          attributionToken: page.attributionToken,
          filter: page.filter,
          pbsSearchEngine: page.pbsSearchEngine,
        };
      }
      return {
        ...acc,
        redirectUrl: page.redirectUrl,
        oemBrandName: page.oemBrandName,
        oemPartNumber: page.oemPartNumber,
        redirectPageType: page.redirectPageType,
        interchangeSearchFlag: page.interchangeSearchFlag,
        lwTestExperience: page.lwTestExperience,
        xFusionQueryId: page.xFusionQueryId,
        attributionToken: page.attributionToken,
        filter: page.filter,
        pbsSearchEngine: page.pbsSearchEngine,
      };
    },
    {
      searchResults: {
        skuRecords: [],
        recordsPerPage: 24,
        searchedKeyword: '',
      },
    } as unknown as ProductSearchResponseModel,
  );

  return {
    mergedData,
    pages: data.pages,
    pageParams: data.pageParams,
    pageNumber: data.pages.length,
  };
};

const {
  useData: useSearchProducts,
  prefetch: prefetchSearchProducts,
  query: searchProductDataQuery,
  getFromCache,
} = createInfiniteQuery<
  ProductSearchResponseModel,
  GetSearchProductDataOptions,
  number,
  BaseMergedData<ProductSearchResponseModel, number>
>(['searchProductData'], getSearchProductData, {
  initialPageParam: 1,
  getNextPageParam: (lastPage, allPages) => {
    const totalRecords = lastPage?.searchResults?.totalNumberOfRecords ?? 0;
    const currentPageNumber = allPages.length;

    // Count actual records loaded across all pages
    const loadedRecords = allPages.reduce((sum, page) => {
      return sum + (page.searchResults?.skuRecords?.length ?? 0);
    }, 0);

    // Only fetch next page if we haven't loaded all records yet
    if (loadedRecords < totalRecords) {
      return currentPageNumber + 1;
    }
    return undefined;
  },
  selectInfiniteQueryData,
});

const handleSortingOptionError = (router: AppRouterInstance) => {
  try {
    // eslint-disable-next-line no-console
    console.error(
      `An error occurred while requesting search results. Invalid sorting option was input`,
    );
    router.replace('/errorPage');
    return;
  } catch {
    throw new Error(`There was a problem redirecting to /errorPage`);
  }
};

export const useSearchProductData = (
  { enabled = true }: { enabled: boolean; vehicleQuestions?: Record<string, string> } = {
    enabled: true,
  },
) => {
  const { state } = useAppState();
  const locale = useLocale();
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const { searchText } = legacyRouter.query;
  const mapFacet = useMapFacet();
  const { acesEnabled } = useAcesFlags();
  const experimentId = useExperimentId();
  const vertexParams = useVertexPersonalizationParams();
  const preferredVehicle = usePreferredVehicle();
  const baseVehicle = {
    year: preferredVehicle?.year,
    makeId: preferredVehicle?.makeId,
    modelId: preferredVehicle?.modelId,
    vehicleTypeId: preferredVehicle?.vehicleTypeId,
  };
  const vehicleId = acesEnabled ? undefined : preferredVehicle?.catalogVehicleId;
  const { data: storeDetailsData } = useStoreDetailsData();
  const storeNumber = storeDetailsData?.storeNumber;
  const [searchState] = useGlobalState('search');
  const { data: headerData } = useHeaderData();
  const servingConfig = useSearchServingConfig();
  const vehicleQuestions = extractVehicleQuestions(
    headerData?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
  ) as Record<string, string>;
  const { pageType } = usePageType();
  const userProfileId = headerData?.myAccountMap?.profileId;
  const rewardsId = headerData?.myAccountMap?.rewardsId ?? '';
  const country = getCountryFromLocale(locale);
  const options = getApiOptions({
    query: legacyRouter.query,
    locale,
    vehicleId: !acesEnabled ? (!vehicleId ? '0' : vehicleId) : vehicleId,
    storeNumber,
    mapFacet,
    preview: showXMPreviewDate(),
    vehicleIdChanged: searchState?.preferredVehicleChanged,
    ...(userProfileId && { userProfileId }),
    acesEndpointEnabled: acesEnabled,
    isBrazil: country === 'BRA',
  });
  const sortParam = options.sort
    ? typeof options.sort === 'string'
      ? options.sort
      : !options.sort.sortOrder
        ? `${options.sort.sortFieldName}`
        : `${options.sort.sortFieldName}-${options.sort.sortOrder}`
    : null;

  const validOptions = country === 'BRA' ? brValidSortingOptions : validSortingOptions;
  const isValidSortingOption = validOptions.has(sortParam ?? '');

  if (options.sort && !isValidSortingOption && pageType === 'search') {
    void handleSortingOptionError(router);
  }

  const normalizedExperimentId = getExperimentId(experimentId, acesEnabled);

  const queryOptions: GetSearchProductDataOptions = {
    ...(!!preferredVehicle && acesEnabled && { baseVehicle }),
    ...(!!vehicleQuestions && acesEnabled && { vehicleQuestions }),
    ...options,
    recordsPerPage: 24,
    skipPdpRedirect: options.skipPdpRedirect ?? false,
    eCookieId: state.eCookieId,
    rewardsId,
    ignoreVehicleSpecificProductsCheck:
      legacyRouter.query.ignoreVehicleSpecificProductsCheck === 'true'
        ? true
        : legacyRouter.query.ignoreVehicleSpecificProductsCheck === 'false'
          ? false
          : undefined,
    acesEnabled,
    ...(acesEnabled && normalizedExperimentId && { experimentId: normalizedExperimentId }),
    ...(vertexParams.shouldInjectParams && {
      visitorId: vertexParams.visitorId,
      userId: vertexParams.userId,
      servingConfig,
    }),
  };

  const retryConfig = useRetryConfigForCategory('SEARCH');

  const infiniteQueryResult = useSearchProducts({
    queryOptions,
    enabled: Boolean(
      enabled &&
      vertexParams.isMyProfileReady &&
      legacyRouter.route === routePaths.searchResult &&
      searchText &&
      typeof searchText === 'string' &&
      searchText !== '' &&
      (storeNumber || locale === countryCodes.mx || locale === countryCodes.ptBr),
    ),
    staleTime: STALE_TIME_30_MINUTES,
    ...retryConfig,
  });

  useQueryResultOnError(infiniteQueryResult, (err) => {
    logger.error({
      message: 'Error fetching search product results',
      meta: {
        error: isAxiosError(err) ? err.response : err,
      },
    });

    try {
      router.replace(routePaths.errorPage);
    } catch {
      throw new Error(`There was a problem redirecting to the error page`);
    }
  });

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    fetchStatus,
    isSuccess,
    isError,
    isFetchingNextPage,
  } = infiniteQueryResult;

  const { mergedData, pages, pageNumber = 1 } = infiniteData ?? {};

  return {
    pages: pages,
    data: searchProductDataSelector(
      mergedData ??
        ({
          searchResults: {
            skuRecords: [],
            recordsPerPage: 24,
            searchedKeyword: '',
          },
        } as ProductSearchResponseModel),
      mapFacet,
      acesEnabled,
      locale,
    ),
    pageNumber,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    isFetching,
    isPending,
    status,
    refetch,
    fetchStatus,
    isSuccess,
    isError,
    isFetchingNextPage,
  };
};

export const getSearchProductDataFromCache = (
  queryClient: QueryClient,
  options: GetSearchProductDataOptions,
) => {
  return getFromCache(queryClient, options);
};

export const prefetchSearchProductDataInfinity = async (
  queryClient: QueryClient,
  options: GetSearchProductDataOptions,
  axiosInstance: AxiosInstance,
) => {
  return prefetchSearchProducts(queryClient, options, axiosInstance);
};

export { searchProductDataQuery };
